import type { ReactNode } from "react";
import type {
  ViewMode,
  Category,
  Task,
  PlanEvent,
} from "../../../shared/types";

interface AppContentProps {
  viewMode: ViewMode;
  sidebarPinned: boolean;
  filteredTasks: Task[];
  filteredEvents: PlanEvent[];
  expandedEvents: PlanEvent[];
  visibleEvents: PlanEvent[];
  activeTasks: Task[];
  events: PlanEvent[];
  defaultCategory: Category;
  showTemplates: boolean;
  onSetShowTemplates: (show: boolean) => void;
  children: {
    todayView: ReactNode;
    weekCalendar: ReactNode;
    quickAdd: ReactNode;
    taskList: ReactNode;
    habitsView: ReactNode;
    agendaView: ReactNode;
    freeSlotsView: ReactNode;
    weeklyView: ReactNode;
    statsView: ReactNode;
    activityLog: ReactNode;
    templatePanel: ReactNode;
    scheduleView: ReactNode;
    eisenhowerView: ReactNode;
    journalView: ReactNode;
    kanbanView: ReactNode;
  };
}

const WIDE_VIEWS = new Set<ViewMode>(["calendar", "kanban", "agenda"]);

export function AppContent({
  viewMode,
  sidebarPinned,
  showTemplates,
  onSetShowTemplates,
  children,
}: AppContentProps) {
  const isWide = WIDE_VIEWS.has(viewMode);

  const mainCls = [
    "flex-1 min-h-0 w-full flex flex-col",
    "scrollbar-custom",
    viewMode === "calendar" ? "overflow-hidden px-4 py-4 gap-4" : "gap-4 px-4 overflow-y-auto",
    // Pour le calendrier, on centre au lieu de décaler
    viewMode === "calendar" && !sidebarPinned ? "md:px-6" : "",
    // Pour les autres vues larges, on garde le décalage
    isWide && viewMode !== "calendar"
      ? sidebarPinned ? "" : "md:pl-[140px] md:pr-6"
      : "",
  ].join(" ");

  const compact = "max-w-2xl mx-auto w-full";
  const scheduleCompact = "max-w-6xl mx-auto w-full";

  return (
    <main className={mainCls} data-ph-no-capture>
      {viewMode === "today" && (
        <div key="today" className={`animate-view-enter ${compact}`}>
          {children.todayView}
        </div>
      )}

      {viewMode === "calendar" && (
        <div key="calendar" className="animate-view-enter flex-1 min-h-0 flex flex-col">
          {children.weekCalendar}
        </div>
      )}

      {viewMode === "tasks" && (
        <div key="tasks" className={`animate-view-enter py-4 ${compact}`}>
          {children.quickAdd}
          {children.taskList}
          <div className="flex justify-center">
            <button
              type="button"
              onClick={() => onSetShowTemplates(!showTemplates)}
              className="text-xs text-ink-muted hover:text-ink hover:underline decoration-ink-muted/30 hover:decoration-ink underline-offset-2 focus:outline-none transition-all py-3"
            >
              {showTemplates ? "Masquer modèles" : "Modèles rapides"}
            </button>
          </div>
          {showTemplates && (
            <div className="pt-3">
              {children.templatePanel}
            </div>
          )}
        </div>
      )}

      {viewMode === "habits" && (
        <div key="habits" className={`animate-view-enter ${compact}`}>
          {children.habitsView}
        </div>
      )}

      {viewMode === "agenda" && (
        <div key="agenda" className="animate-view-enter">
          {children.agendaView}
        </div>
      )}

      {viewMode === "free-slots" && (
        <div key="free-slots" className={`animate-view-enter ${compact}`}>
          {children.freeSlotsView}
        </div>
      )}

      {viewMode === "weekly" && (
        <div key="weekly" className={`animate-view-fade ${compact}`}>
          {children.weeklyView}
        </div>
      )}

      {viewMode === "schedule" && (
        <div key="schedule" className={`animate-view-enter ${scheduleCompact}`}>
          {children.scheduleView}
        </div>
      )}

      {viewMode === "eisenhower" && (
        <div key="eisenhower" className={`animate-view-enter ${compact} py-12`}>
          {children.eisenhowerView}
        </div>
      )}

      {viewMode === "journal" && (
        <div key="journal" className={`animate-view-enter ${compact}`}>
          {children.journalView}
        </div>
      )}

      {viewMode === "kanban" && (
        <div key="kanban" className="animate-view-enter py-12">
          <div className="max-w-4/5 ml-20">
            {children.kanbanView}
          </div>
        </div>
      )}

      {viewMode === "stats" && (
        <div key="stats" className={`animate-view-enter ${compact}`}>
          {children.statsView}
          {children.activityLog}
        </div>
      )}
    </main>
  );
}
